import * as Sentry from '@sentry/react';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT ||
  (import.meta.env.PROD ? 'production' : 'development');
const release = import.meta.env.VITE_SENTRY_RELEASE;

const SENSITIVE_KEY = /email|password|token|authorization|cookie|secret|api[_-]?key/i;

function scrubValue(key: string, value: unknown): unknown {
  if (SENSITIVE_KEY.test(key)) return '[Filtered]';
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return scrubObject(value as Record<string, unknown>);
  }
  return value;
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    out[key] = scrubValue(key, value);
  }
  return out;
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
    delete event.user.username;
  }
  if (event.request) {
    delete event.request.cookies;
    if (event.request.headers && typeof event.request.headers === 'object') {
      event.request.headers = scrubObject(
        event.request.headers as Record<string, unknown>,
      ) as typeof event.request.headers;
    }
    if (event.request.data && typeof event.request.data === 'object') {
      event.request.data = scrubObject(event.request.data as Record<string, unknown>);
    }
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      if (!crumb.data) return crumb;
      return { ...crumb, data: scrubObject(crumb.data as Record<string, unknown>) };
    });
  }
  return event;
}

Sentry.init({
  dsn,
  enabled: Boolean(dsn),
  environment,
  ...(release ? { release } : {}),
  sendDefaultPii: false,
  enableLogs: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
  ],
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: import.meta.env.PROD ? 0.25 : 0,
  beforeSend,
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
  ],
});

function isSentryConsoleTestEnabled(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    return localStorage.getItem('lccto_sentry_debug') === '1';
  } catch {
    return false;
  }
}

if (dsn) {
  window.triggerSentryTest = (throwUncaught = false) => {
    if (!isSentryConsoleTestEnabled()) {
      console.warn(
        '[sentry:engine] Set localStorage lccto_sentry_debug=1 and reload for prod smoke test.',
      );
      return undefined;
    }
    const err = new Error('LCCTO engine — Sentry test (console)');
    if (throwUncaught) throw err;
    const eventId = Sentry.captureException(err);
    console.info('[sentry:engine] Test error sent.', { eventId, environment });
    return eventId;
  };
  if (import.meta.env.DEV || isSentryConsoleTestEnabled()) {
    console.info('[sentry:engine] triggerSentryTest() ready');
  }
}

declare global {
  interface Window {
    /** Dev, or prod after localStorage lccto_sentry_debug=1 + reload. */
    triggerSentryTest?: (throwUncaught?: boolean) => string | undefined;
  }
}

export { Sentry };

const REPORTED = Symbol('lcctoSentryReported');

function markErrorReported(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const o = error as Record<symbol, unknown>;
  if (o[REPORTED]) return true;
  o[REPORTED] = true;
  return false;
}

/** Report Supabase table/client failures with lccto.layer=ui tags. */
export function captureSupabaseFailure(
  table: string,
  operation: string,
  error: unknown,
  context?: Record<string, string>,
): void {
  if (!dsn || markErrorReported(error)) return;
  const message = error instanceof Error ? error.message : String(error);
  Sentry.withScope((scope) => {
    scope.setTag('lccto.layer', 'ui');
    scope.setTag('lccto.operation', `${table}.${operation}`);
    scope.setTag('lccto.table', table);
    if (context) {
      for (const [k, v] of Object.entries(context)) {
        scope.setExtra(k, v);
      }
    }
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(`engine ui: ${table}.${operation} — ${message}`, 'error');
    }
  });
}

/** Edge function failures invoked from Engine UI (cdn-assets, etc.). */
export function captureEdgeFunctionFailure(
  functionName: string,
  operation: string,
  error: unknown,
  context?: Record<string, string>,
): void {
  captureSupabaseFailure(`edge:${functionName}`, operation, error, context);
}

/** Shorthand for UI modules — same as captureSupabaseFailure. */
export function reportUiError(
  table: string,
  operation: string,
  error: unknown,
  context?: Record<string, string>,
): void {
  captureSupabaseFailure(table, operation, error, context);
}

/** UI milestone — start/success breadcrumbs with lccto.milestone tag. */
export function emitUiMilestone(
  operation: string,
  phase: 'start' | 'success',
  context?: Record<string, string>,
): void {
  if (!dsn) return;
  Sentry.withScope((scope) => {
    scope.setTag('lccto.layer', 'ui');
    scope.setTag('lccto.operation', operation);
    scope.setTag('lccto.milestone', phase);
    if (context) {
      for (const [k, v] of Object.entries(context)) {
        scope.setExtra(k, v);
      }
    }
    Sentry.logger.info(`engine ui: ${operation}.${phase}`);
    Sentry.captureMessage(`engine ui: ${operation}.${phase}`, 'info');
  });
}

/** Structured UI log — forwarded when enableLogs is on. */
export function logUi(
  level: 'info' | 'warn' | 'error',
  message: string,
  context?: Record<string, string>,
): void {
  if (!dsn) return;
  const extra = context ?? {};
  if (level === 'error') Sentry.logger.error(message, extra);
  else if (level === 'warn') Sentry.logger.warn(message, extra);
  else Sentry.logger.info(message, extra);
}
